import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useRealtimeEvent } from '@/lib/realtime/socket';
import type { IncomingCallPayload } from '@/lib/api/types.videoCall';

/**
 * Global incoming-call toast — mounted once in AppShell so an invite reaches
 * the user regardless of what page they're on (unlike the call room's own
 * signaling events, this ordinary tenant/user broadcast is low-frequency
 * enough not to need the room-scoped mechanism).
 */
export function IncomingCallToast() {
  const [incoming, setIncoming] = useState<IncomingCallPayload | null>(null);
  const navigate = useNavigate();

  useRealtimeEvent<IncomingCallPayload>('call:incoming', (payload) => setIncoming(payload));

  if (!incoming) return null;

  function accept() {
    if (!incoming) return;
    navigate(`/projects/${incoming.relatedId}?joinCall=${incoming.callId}`);
    setIncoming(null);
  }

  return (
    <div className="fixed bottom-4 right-4 z-[60] w-72 rounded-lg border border-ink-900/10 bg-white p-4 shadow-popover">
      <p className="text-[12.5px] font-semibold text-ink-800">Incoming video call</p>
      <p className="mt-0.5 text-[11.5px] text-ink-500">Someone is calling on a project you're part of.</p>
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="text-[11.5px] font-medium text-ink-500 hover:text-ink-700" onClick={() => setIncoming(null)}>
          Decline
        </button>
        <Button size="sm" onClick={accept}>Join</Button>
      </div>
    </div>
  );
}
